import { useEffect, useSyncExternalStore } from 'react';
import { busyTracker } from './busyTracker';

/** Reactively read whether an interruption-sensitive operation (upload/delete/…) is running. */
export function useBusy(): boolean {
  return useSyncExternalStore(
    busyTracker.subscribe,
    busyTracker.getSnapshot,
    () => false, // server snapshot (idle during SSR)
  );
}

/**
 * Confirm an in-app navigation that would leave a long-running operation (a bulk upload or
 * bulk delete) behind. The `beforeunload` guard only fires on real browser unloads
 * (refresh/close/external nav); hash-routed in-app navigation (sidebar page switch, sign
 * out, back/forward) just re-renders without unloading, so those must be guarded explicitly
 * with this. Returns true when it's safe to proceed — nothing running, or the user confirmed.
 */
export function confirmLeaveWhileBusy(
  message = 'An operation is still in progress. If you leave this page its progress will no longer be shown. Continue?',
): boolean {
  if (!busyTracker.isActive()) return true;
  if (typeof window === 'undefined') return true;
  return window.confirm(message);
}

/**
 * While an operation is in progress, warn the user before they leave/reload/close the tab —
 * a navigation would abort the in-flight requests (S3 PUTs, delete calls). Browsers show
 * their own generic "Leave site?" prompt; custom text is intentionally ignored by modern
 * browsers.
 *
 * The listener is attached ONCE for the component's lifetime and consults the live store at
 * fire time, rather than being added/removed on a `busy` transition. This matches the other
 * guards (reload gate, nav confirms) which all read `busyTracker.isActive()` synchronously,
 * and removes any dependency on a React re-render landing before the user hits refresh — so
 * it fires reliably even during an operation that triggers many rapid re-renders (e.g. a
 * bulk delete updating progress per item). No-op while idle.
 */
export function useBeforeUnloadWhileBusy(): void {
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!busyTracker.isActive()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);
}
